// Public newsletter archive: a grid of published issues with an optional cover
// thumbnail. Reads list_published_newsletters (anon RPC), which returns
// slug/yearMonth/title/publishedAt + an approved cover (if any). Cards are real
// anchors (so middle-click / open-in-new-tab work) with a hover-lift, matching
// the A6 openable affordance for a public links list.

import React from 'react';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {
  newsletterPublicPhotoUrl,
  formatYearMonth,
  buildNewsletterIssuePath,
  withNewsletterKey,
} from '../lib/newsletterApi.js';

// One entry of the list_published_newsletters payload — ONLY the fields this
// grid renders. The RPC builds the object in SQL
// (supabase-migrations/153_newsletter_archive_link.sql: jsonb_build_object of
// slug / yearMonth / title / publishedAt / cover), and the client has no
// generated DB types, so `data` arrives untyped; the names below are copied
// from that jsonb_build_object rather than guessed, and `publishedAt` is
// deliberately absent because the archive card never reads it.
export type PublishedNewsletterSummary = {
  slug: string;
  yearMonth?: string | null;
  title?: string | null;
  cover?: {storagePath?: string | null; altText?: string | null} | null;
};

// `issues` is whatever the caller has in state, which is `null` before the
// fetch settles and stays `null` when the access key is rejected — hence the
// Array.isArray guard below rather than a required array.
type NewsletterArchiveProps = {
  sb: SupabaseClient;
  issues?: readonly PublishedNewsletterSummary[] | null;
  accessKey?: string | null;
};

export default function NewsletterArchive({sb, issues, accessKey}: NewsletterArchiveProps) {
  const list = Array.isArray(issues) ? issues : [];

  return (
    <div className="nl-archive">
      <header className="nl-archive-header">
        <div className="nl-kicker">White Creek Farm</div>
        <h1 className="nl-title">Monthly Review</h1>
        <p className="nl-archive-sub">A look back at each month on the farm — animals, milestones, and good news.</p>
      </header>

      {list.length === 0 ? (
        <p className="nl-empty">No issues have been published yet. Check back soon.</p>
      ) : (
        <ul className="nl-archive-grid">
          {list.map((it) => {
            const coverUrl = it.cover && it.cover.storagePath ? newsletterPublicPhotoUrl(sb, it.cover.storagePath) : '';
            return (
              <li key={it.slug}>
                <a className="nl-archive-card" href={withNewsletterKey(buildNewsletterIssuePath(it.slug), accessKey)}>
                  {coverUrl ? (
                    <span className="nl-archive-thumb">
                      <img src={coverUrl} alt={(it.cover && it.cover.altText) || ''} loading="lazy" />
                    </span>
                  ) : (
                    <span className="nl-archive-thumb nl-archive-thumb-empty" aria-hidden="true" />
                  )}
                  <span className="nl-archive-body">
                    <span className="nl-archive-month">{formatYearMonth(it.yearMonth)}</span>
                    <span className="nl-archive-title">{it.title}</span>
                    <span className="nl-archive-read">Read ›</span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}

      <p className="nl-foot-micro" style={{marginTop: 32}}>
        Private archive — this link refreshes each month and expires when a new one is issued. Please don’t forward it.
      </p>
    </div>
  );
}
